<template>
  <div class="guide-sales-tax">
    <h2>Sales tax on e-sign payments</h2>
    <p>
      When your packages collect money from signers, the payment may be subject to sales
      tax (or VAT, GST, and similar taxes outside the US). Whether tax applies, and at
      what rate, depends on where the signer lives and what the payment is for. This
      guide explains how the platform calculates the right amount, what you set up once
      on your merchant account, and how you classify each payment.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Who's responsible for tax</h2>
    <p>
      You are. You are the party receiving money from the signer, and your merchant
      account (the payment account that receives signer funds, separate from the
      platform) holds those funds.
    </p>
    <p>
      The platform calculates tax, passes it through your merchant account, and records
      a breakdown on every charge. It does not keep any portion of the tax, and it does
      not file returns on your behalf. A separate platform fee is charged on each
      successful transaction and itemized on your statement; that fee is never mixed
      with tax. Full terms are in the Payment Collection Addendum (v1.2) in your
      <a href="/Settings/PaymentSettings">Payment Settings</a>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Background: how the rules have tightened</h2>
    <p>
      Sales tax on internet payments used to be an afterthought. A 1992 Supreme Court
      decision (Quill Corp. v. North Dakota) held that states could only require tax
      collection from businesses with a physical presence inside their borders. For
      most of the early internet era, remote and online businesses did not collect,
      and states could not compel them.
    </p>
    <p>
      That changed in 2018 with South Dakota v. Wayfair, which allowed states to
      require tax collection based on economic presence alone. Within about two years,
      every US state with a sales tax passed an "economic nexus" law that triggers
      collection when an out-of-state business crosses a threshold of in-state payment
      volume (commonly $100,000) or transactions (commonly 200). Several states have
      since dropped the transaction-count side of the threshold, so a small number of
      higher-value transactions is enough on its own.
    </p>
    <p>
      Enforcement has tightened steadily since. States have expanded which services
      and digital goods are taxable, invested in audit capacity, and begun sharing
      data across revenue departments to find non-compliant collectors. The long arc
      has been toward broader tax bases, not narrower ones.
    </p>

    <p>A few takeaways for anyone collecting payments through an internet platform for the first time:</p>
    <ul>
      <li>
        <strong>"I only do business online" is no longer a reason not to collect.</strong>
        It may have been in 2015. It has not been in most US states since 2018.
      </li>
      <li>
        <strong>Services, SaaS, digital goods, and rentals are increasingly in scope.</strong>
        Historically many states only taxed tangible personal property. That is no
        longer a safe assumption in most states.
      </li>
      <li>
        <strong>Marketplace platforms are not the same as payment platforms.</strong>
        When a merchant lists products on Amazon, Etsy, or eBay, those marketplaces
        collect and remit tax on the merchant's behalf under state "marketplace
        facilitator" laws. A payment platform embedded in a signing workflow (such as
        this one) is not a marketplace. You are the merchant of record on every
        signer-paid transaction: the platform calculates, you collect, and you file.
      </li>
      <li>
        <strong>International rules have moved in the same direction.</strong>
        The UK, EU, Canada, Australia, and most OECD jurisdictions have tightened VAT
        and GST on cross-border digital services over roughly the same decade. If you
        collect payments from signers in any of those markets, similar thresholds and
        registrations apply under local rules.
      </li>
    </ul>

    <p>
      None of this is unique to our platform or unusual for a modern business. It is
      simply the environment you are operating in, and knowing the shape of it makes
      the rest of this guide easier to work through.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Do you have sales tax liability?</h2>
    <p>
      Before you pick a declaration in Payment Settings, you need a working answer to
      the question the declaration asks: do the payments you collect actually require
      you to charge sales tax? The platform cannot answer this, and neither can any
      software vendor. It depends on what you are collecting payment for, where you
      operate, and where your signers are. A few general rules of thumb to orient
      yourself:
    </p>

    <ul>
      <li>
        <strong>Start with your home state.</strong>
        If your business has any physical presence in a US state (an office, employees,
        a warehouse, or a home-based operation), that state is almost certainly the
        first place to check. Most businesses that owe sales tax owe it at home first.
      </li>
      <li>
        <strong>Know what is taxable.</strong>
        States vary sharply on what is subject to sales tax. Tangible goods are usually
        taxable; professional services often are not; software-as-a-service, digital
        goods, and rentals fall in different buckets depending on the state. Real-estate
        leases, earnest-money deposits, and certain business-to-business transactions
        are commonly exempt or out of scope entirely.
      </li>
      <li>
        <strong>Watch for economic nexus.</strong>
        Even without physical presence, you can trigger a collection obligation in
        another state by exceeding its economic threshold, typically $100,000 in
        payment volume or 200 transactions per year to signers in that state. The
        threshold applies regardless of where your own business is based.
      </li>
      <li>
        <strong>When in doubt, ask the authority directly.</strong>
        In the US, the relevant office is your state's Department of Revenue,
        Comptroller, Department of Taxation, or equivalent. (The Secretary of State
        handles business registrations, not sales tax, and is a common wrong turn.)
        Most tax agencies publish written guidance and will answer taxpayer questions
        by phone or email. Outside the US, the equivalents include HMRC in the UK,
        CRA in Canada, and the ATO in Australia.
      </li>
      <li>
        <strong>Consider hiring help.</strong>
        If you collect at any material scale, or across multiple states, a sales-tax
        CPA or a tax-compliance specialist is inexpensive insurance. They can review
        your specific situation once and tell you which registrations to file.
      </li>
    </ul>

    <p>
      This guide is general information, not tax advice, and your specific facts
      matter. The declaration you record in Payment Settings is your own
      determination, made under your own name. If you are genuinely unsure, ask the
      taxing authority or a qualified tax professional before recording either choice.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Setting up tax collection</h2>
    <p>
      Tax collection is configured per merchant account, inside your Payment Settings
      page. The platform cannot register on your behalf: registration is a legal
      commitment that has to be made in your business's name.
    </p>

    <p>One-time setup:</p>
    <ol>
      <li>
        Open <strong>Payment Settings</strong> in the portal.
      </li>
      <li>
        Click <strong>I'm required to collect tax</strong>. The page expands with
        embedded setup panels.
      </li>
      <li>
        Enter your business's head-office address. This determines which tax laws
        apply to you (for example, which US state you are headquartered in).
      </li>
      <li>
        Add your tax registrations: the list of states or countries where you are
        registered to collect tax.
      </li>
      <li>
        Pick your default product tax classification: the category that best describes
        most of the payments you collect. You can override it per template or per
        signer for special cases.
      </li>
    </ol>

    <p>
      When setup is complete, the Payment Settings card shows
      <strong>Tax collection: Active, N registrations</strong>. Until then,
      signer-paid sends are blocked with an error pointing you back to finish setup.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>What is "nexus"?</strong> You will see the word in the setup screens.
      It is tax-law jargon for a connection strong enough that a jurisdiction can
      require you to collect tax there. You gain nexus in a US state either by having
      a physical presence (office, employees, inventory) or by exceeding that state's
      economic threshold (typically $100,000 or 200 transactions per year). Your tax
      dashboard warns you when you are approaching a threshold in any state.
    </q-banner>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Tip:</strong> The rate lookups and filings math run through our payment
      processing partner. You configure everything inside Payment Settings; there is
      no separate dashboard to visit. Changes save directly to your merchant account.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Your tax-collection declaration</h2>
    <p>
      Not every sender has to charge sales tax. Some long-term leases are exempt,
      earnest-money deposits on real-estate transactions are not usually subject to
      sales tax, non-US businesses may not need to charge US sales tax, and some
      business-to-business payments are exempt. Before any signer-paid ceremony can
      run, Payment Settings asks you to make a one-time declaration.
    </p>

    <ul>
      <li>
        <strong>I'm required to collect tax.</strong>
        The setup described above runs, and every signer-paid charge has the correct
        tax added automatically.
      </li>
      <li>
        <strong>I'm NOT required to collect tax.</strong>
        You affirm, with a reason on file (for example, "long-term lease, not taxable
        in my state"), that the payments you collect are not subject to tax. Tax
        calculation is skipped on all your signer-paid charges, and signers are billed
        the base amount only. You can change this later if your situation changes.
      </li>
    </ul>

    <p>
      Both choices are written to your account's audit trail with the user, timestamp,
      and reason, so there is a durable record of who made the determination and when.
      The platform cannot make the call for you, which is why it asks you to state it
      explicitly.
    </p>

    <p>
      Until a declaration is recorded, signer-paid ceremonies are blocked with a clear
      message asking the sender to complete their tax setup. This is intentional: the
      platform never silently runs, or silently skips, tax calculation before you have
      told it which is correct for you.
    </p>

    <q-separator class="q-my-lg" />

    <h2>How tax is calculated</h2>

    <h3>The three inputs</h3>
    <p>For each signer-paid charge, the tax engine needs three things:</p>
    <ol>
      <li>
        <strong>Where the signer lives.</strong>
        Pulled from the billing address on the payment method the signer enters during
        the ceremony. This resolves to a country, state or province, and locality.
      </li>
      <li>
        <strong>What the payment is for.</strong>
        Every payment carries a "product tax code" that classifies it as a physical
        product, a subscription, a rental, a professional service, a lease payment, an
        earnest-money deposit, and so on. The tax engine uses the code to pick the
        right rate. Common examples: general services, rental of equipment, lease of
        real property, software-as-a-service.
      </li>
      <li>
        <strong>Where you are registered.</strong>
        The list of states or countries on your merchant account.
      </li>
    </ol>
    <p>
      The platform supplies the first two. Your merchant account supplies the third.
      Together they determine whether tax applies, and at what rate.
    </p>

    <q-banner class="bg-grey-2 text-grey-9 q-my-md" rounded>
      <strong>Worked example.</strong> A signer in Austin, Texas completes payment on
      a commercial lease you sent. The signer's location resolves to Travis County,
      Texas. The product tax code on the package is "lease of real property." Your
      merchant account is registered in Texas. The engine applies the Texas rate for
      real-property leases and itemizes state and county portions on the receipt.
    </q-banner>

    <h3>Picking the product tax code</h3>
    <p>
      Every payment needs one product tax code. You can set the code at three levels,
      and the most specific one wins:
    </p>
    <ul>
      <li>
        <strong>Per signer on a package</strong>, configured when you add a paying
        signer in the package builder. Useful when different signers on the same
        package are paying for different things.
      </li>
      <li>
        <strong>Per role on a template.</strong>
        A package created from the template inherits the role's tax code onto the
        matching signer at creation time.
      </li>
      <li>
        <strong>Your account default</strong>, set once in
        <a href="/Settings/PaymentSettings">Payment Settings</a>. This is the fallback
        when neither the signer nor the template level specifies one.
      </li>
    </ul>

    <p>
      If none of the three is set, the send fails. Charging without a classification
      would mis-label the product for jurisdictions that care (a lease is taxed
      differently from a rental or a service), which is a worse outcome than a loud
      failure that prompts you to pick one. Setting a default in Payment Settings
      prevents this error.
    </p>

    <q-banner class="bg-grey-2 text-grey-9 q-my-md" rounded>
      <strong>Worked example.</strong> Your account default is "general services."
      Your "Equipment Lease" template sets the role tax code to "rental of tangible
      personal property." For one specific package built from that template, you
      assign a signer paying for a long-term rental and override just that signer's
      code to "lease of tangible personal property." The signer-level code wins over
      both the template and the account default.
    </q-banner>

    <h3>What happens at checkout</h3>
    <p>
      When the last signer completes the ceremony, the platform asks the tax engine
      to compute the final amount. Two outcomes are possible in normal use:
    </p>
    <ul>
      <li>
        <strong>You are registered in the signer's location.</strong>
        The engine returns a tax amount and a breakdown of which jurisdictions
        applied. The signer is charged base plus tax in one transaction, and both
        amounts flow into your merchant balance. The breakdown is stored on the
        payment record.
      </li>
      <li>
        <strong>You are not registered in the signer's location.</strong>
        The engine returns zero tax with a "not collecting" reason. The signer is
        charged the base amount only, and the reason is recorded on the payment.
      </li>
    </ul>

    <p>
      If the calculation itself fails, the payment fails with an error telling the
      signer to contact the sender (you), and the fix is to finish setup in Payment
      Settings. In normal use this does not occur, because setup status is verified
      at send time and incomplete setup blocks the send before any signer is affected.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Refunds and chargebacks</h2>
    <p>
      Tax follows the payment. When you refund a signer-paid charge, the refund
      includes any tax that was collected, so the signer gets back exactly what they
      paid. Your merchant balance is debited for the base amount plus the tax portion.
      The refund appears in your tax dashboard reports, and you reflect it in your
      next filing for the affected period. The platform does not file on your behalf.
    </p>
    <p>
      Chargebacks work the same way. If a signer disputes a charge and wins, your
      merchant account is debited for the full amount including tax. The platform's
      transaction fee is refunded proportionally on refunds and chargebacks; the tax
      portion was always yours, never the platform's, so there is nothing for the
      platform to refund on the tax side.
    </p>

    <q-separator class="q-my-lg" />

    <h2>A note on non-US transactions</h2>
    <p>
      VAT, GST, and equivalent taxes use the same calculation engine and the same
      Payment Settings flow. Step 4 of setup accepts country-level registrations
      alongside US states, and product tax codes map to the classifications those
      jurisdictions use. The guidance above (declarations, merchant-account
      responsibility, refunds, chargebacks) applies identically. What differs is
      thresholds and filing cadences, and your tax dashboard reflects the rules for
      each jurisdiction you are registered in.
    </p>
  </div>
</template>

<script>
export default {
    name: 'GuideSalesTax'
};
</script>
